import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  Container,
  Grid,
  Header,
  Segment,
  Button,
  Icon,
  List,
  Card,
  Breadcrumb,
  Dimmer,
  Loader,
  Message,
} from 'semantic-ui-react'
import { fetchSolutionBySlug } from '../../../lib/datocms'
import './Solutions.css'

const SolutionDetail = () => {
  const { slug } = useParams()
  const [solution, setSolution] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadSolution()
  }, [slug])

  const loadSolution = async () => {
    try {
      setLoading(true)
      const data = await fetchSolutionBySlug(slug)
      if (!data) {
        setError('Solution not found')
      } else {
        setSolution(data)
      }
    } catch (err) {
      console.error('Error loading solution:', err)
      setError('Failed to load solution. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Dimmer active inverted>
        <Loader size='large'>Loading Solution...</Loader>
      </Dimmer>
    )
  }

  if (error) {
    return (
      <Container style={{ marginTop: '2em' }}>
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
        <Button as={Link} to='/solutions' primary>
          <Icon name='arrow left' /> Back to Solutions
        </Button>
      </Container>
    )
  }

  if (!solution) {
    return null
  }

  return (
    <div className="solution-detail-page">
      {/* Breadcrumb */}
      <Container style={{ paddingTop: '20px' }}>
        <Breadcrumb>
          <Breadcrumb.Section as={Link} to='/'>Home</Breadcrumb.Section>
          <Breadcrumb.Divider />
          <Breadcrumb.Section as={Link} to='/solutions'>Solutions</Breadcrumb.Section>
          <Breadcrumb.Divider />
          <Breadcrumb.Section active>{solution.title}</Breadcrumb.Section>
        </Breadcrumb>
      </Container>

      {/* Hero Section */}
      <Segment className="solution-hero" vertical>
        <Container>
          <Grid columns={2} stackable verticalAlign='middle'>
            <Grid.Column>
              <Header as='h1' inverted>
                {solution.title}
              </Header>
              <p className="solution-hero-description">
                {solution.shortDescription}
              </p>
              {solution.ctaText && (
                <Button 
                  primary 
                  size='large' 
                  as={solution.ctaLink ? 'a' : Link}
                  href={solution.ctaLink}
                  to={!solution.ctaLink ? '/contact-us' : undefined}
                >
                  {solution.ctaText}
                  <Icon name='arrow right' />
                </Button>
              )}
            </Grid.Column>
            <Grid.Column>
              {solution.image?.url && (
                <img 
                  src={solution.image.url} 
                  alt={solution.image.alt || solution.title}
                  style={{ width: '100%', borderRadius: '8px' }}
                />
              )}
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* Description Section */}
      <Segment vertical style={{ padding: '80px 0' }}>
        <Container>
          <Header as='h2' textAlign='center'>Overview</Header>
          <p style={{ fontSize: '1.1em', lineHeight: '1.8' }}>
            {solution.description}
          </p>
        </Container>
      </Segment>

      {/* Features Section */}
      {solution.features && solution.features.length > 0 && (
        <Segment vertical style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
          <Container>
            <Header as='h2' textAlign='center' style={{ marginBottom: '3em' }}>
              Key Features
            </Header>
            <Grid columns={3} stackable>
              {solution.features.map(feature => (
                <Grid.Column key={feature.id}>
                  <div className="feature-box">
                    <Icon name='check circle' size='large' color='green' />
                    <Header as='h3'>{feature.title}</Header>
                    <p>{feature.description}</p>
                  </div>
                </Grid.Column>
              ))}
            </Grid>
          </Container>
        </Segment>
      )}

      {/* Benefits Section */}
      {solution.benefits && solution.benefits.length > 0 && (
        <Segment vertical style={{ padding: '80px 0' }}>
          <Container>
            <Grid columns={2} stackable>
              <Grid.Column>
                <Header as='h2'>Benefits</Header>
                <List bulleted size='large'>
                  {solution.benefits.map((benefit, index) => (
                    <List.Item key={index}>{benefit}</List.Item>
                  ))}
                </List>
              </Grid.Column>
              <Grid.Column>
                <Segment placeholder>
                  <Header icon>
                    <Icon name='chart line' />
                    Benefits Visualization
                  </Header>
                </Segment>
              </Grid.Column>
            </Grid>
          </Container>
        </Segment>
      )}

      {/* Use Cases Section */}
      {solution.useCases && solution.useCases.length > 0 && (
        <Segment vertical style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
          <Container>
            <Header as='h2' textAlign='center' style={{ marginBottom: '3em' }}>
              Use Cases
            </Header>
            <Grid columns={2} stackable>
              {solution.useCases.map((useCase, index) => (
                <Grid.Column key={index}>
                  <Segment>
                    <Icon name='lightbulb' color='yellow' size='large' />
                    <p style={{ marginTop: '10px' }}>{useCase}</p>
                  </Segment>
                </Grid.Column>
              ))}
            </Grid>
          </Container>
        </Segment>
      )}

      {/* Related Solutions */}
      {solution.relatedSolutions && solution.relatedSolutions.length > 0 && (
        <Segment vertical style={{ padding: '80px 0' }}>
          <Container>
            <Header as='h2' textAlign='center' style={{ marginBottom: '3em' }}>
              Related Solutions
            </Header>
            <Card.Group itemsPerRow={3} stackable>
              {solution.relatedSolutions.map(related => (
                <Card key={related.id} as={Link} to={`/solutions/${related.slug}`}>
                  <Card.Content>
                    {related.icon?.url && (
                      <div className="solution-icon">
                        <img src={related.icon.url} alt={related.icon.alt || related.title} />
                      </div>
                    )}
                    <Card.Header>{related.title}</Card.Header>
                    <Card.Description>{related.shortDescription}</Card.Description>
                  </Card.Content>
                  <Card.Content extra>
                    <Button primary fluid>
                      Learn More <Icon name='arrow right' />
                    </Button>
                  </Card.Content>
                </Card>
              ))}
            </Card.Group>
          </Container>
        </Segment>
      )}

      {/* CTA Section */}
      <Segment className="solution-cta" inverted vertical>
        <Container textAlign='center'>
          <Header as='h2' inverted>
            Ready to Get Started with {solution.title}?
          </Header>
          <p style={{ fontSize: '1.1em', marginBottom: '2em' }}>
            Transform your business operations with our comprehensive solution.
          </p>
          <Button size='large' inverted as={Link} to='/signup'>
            Start Free Trial
          </Button>
          <Button size='large' inverted basic as={Link} to='/contact-us'>
            Contact Sales
          </Button>
        </Container>
      </Segment>
    </div>
  )
}

export default SolutionDetail